import React from 'react';
import {
  ArrowRight,
  ClipboardCheck,
  FileText,
  MessageCircle,
  PhoneCall,
  PhoneOff,
  UserCheck,
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { Button } from '@/Components/ui/button';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import useSEO from '../HomePage/useSEO';
import ScrollReveal from '../HomePage/ScrollReveal';
import {
  IndustryFlowStyles,
  IndustryWorkflow,
  ProblemGrid,
  Section,
  SectionIntro,
} from '../Industries/industryUi';

const VOICE_SCREENING_PATH = '/hirewithus/ai-call-screening';

const CONVERSATION_FLOW = [
  {
    title: 'Choose the screening',
    detail: 'Run a short HR screening or a deeper technical screening based on the role.',
    icon: ClipboardCheck,
  },
  {
    title: 'AI voice call',
    detail: 'The voice agent calls the candidate and asks the relevant questions.',
    icon: PhoneCall,
  },
  {
    title: 'WhatsApp Q&A',
    detail: 'Candidates can complete the available opt-in screening questions in WhatsApp.',
    icon: MessageCircle,
  },
  {
    title: 'Conversation evidence',
    detail: 'Review the call recording, transcript, and post-call evaluation when available.',
    icon: FileText,
  },
  {
    title: 'Recruiter review',
    detail: 'A recruiter decides the next hiring step using the available evidence.',
    icon: UserCheck,
  },
];

const VERIFIED_CHECKS = [
  {
    title: 'Interest',
    detail: 'The HR voice screening records whether the candidate is interested in the role.',
  },
  {
    title: 'Current and expected compensation',
    detail: 'The HR voice screening can collect current and expected CTC.',
  },
  {
    title: 'Notice period',
    detail: 'Collect notice-period information before recruiter follow-up.',
  },
  {
    title: 'Role-specific questions',
    detail: 'Voice questions are generated from the job description and required skills.',
  },
  {
    title: 'Technical depth',
    detail: 'Technical screening evaluates the depth of answers to role-relevant questions.',
  },
];

const VERIFIED_GROUPS = [
  {
    label: 'Candidate details',
    items: VERIFIED_CHECKS.slice(0, 3),
  },
  {
    label: 'Role context',
    items: VERIFIED_CHECKS.slice(3),
  },
];

const OUTCOMES = [
  {
    title: 'Qualified → next hiring step',
    detail: 'The recruiter reviews the score or recommendation and decides who should continue.',
    icon: UserCheck,
  },
  {
    title: 'Not qualified → pipeline update',
    detail: 'The screening result stays with the candidate record for the recruiter to update the pipeline.',
    icon: ClipboardCheck,
  },
  {
    title: 'No response → follow-up',
    detail: 'No answer, busy, voicemail, and callback outcomes are recorded for follow-up or retry.',
    icon: PhoneOff,
  },
];

export default function VoiceWhatsAppRecruiting() {
  useSEO({
    title: 'AI Voice & WhatsApp Recruitment | SearchJob.ai',
    description:
      'Engage candidates and support screening and qualification with AI voice and WhatsApp recruiting.',
    canonical: 'https://searchjob.ai/solutions/voice-whatsapp',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <Header />
      <main className="pt-16">
        <Hero />
        <ScrollReveal>
          <ConversationFlow />
        </ScrollReveal>
        <ScrollReveal>
          <VerifiedChecks />
        </ScrollReveal>
        <ScrollReveal>
          <Outcomes />
        </ScrollReveal>
      </main>
      <Footer />
      <IndustryFlowStyles />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative isolate overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-primary/5 blur-3xl" />
      </div>
      <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 pt-12 pb-16 sm:px-6 lg:grid-cols-2 lg:gap-14 lg:px-8 lg:pt-16">
        <div className="text-center lg:text-left">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <PhoneCall className="h-3.5 w-3.5" aria-hidden="true" />
            AI voice + WhatsApp
          </p>
          <h1 className="mb-6 text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-[3.4rem] xl:text-6xl">
            Your Candidates <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">Already Answer the Phone.</span>
          </h1>
          <p className="mx-auto mb-8 max-w-xl text-lg leading-relaxed text-muted-foreground sm:text-xl lg:mx-0">
            Let AI talk to them. SearchJob can engage candidates through AI calls and WhatsApp messages to verify hiring information.
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
            <Button
              asChild
              size="lg"
              className="!h-12 !rounded-full !bg-primary px-8 text-base font-semibold !text-primary-foreground shadow-lg shadow-primary/25 group hover:!bg-primary/90"
            >
              <a href={VOICE_SCREENING_PATH}>
                Explore AI Voice Screening
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="!h-12 !rounded-full bg-card/70 px-8 text-base font-semibold backdrop-blur-md"
            >
              <a href="#candidate-conversation">See Candidate Conversation</a>
            </Button>
          </div>
        </div>
        <VoiceArtwork />
      </div>
    </section>
  );
}

function VoiceArtwork() {
  return (
    <aside className="relative self-start" aria-label="AI voice recruiting artwork">
      <div className="pointer-events-none absolute -inset-4 -z-10 rounded-[3rem] bg-gradient-to-tr from-primary/20 via-emerald-500/10 to-teal-500/20 blur-3xl" />
      <p className="relative mx-auto mb-5 max-w-[22rem] text-xs font-semibold uppercase tracking-wider text-muted-foreground lg:mb-8 lg:ml-auto lg:mr-0">
        AI call screening
      </p>
      <figure className="relative mx-auto max-w-[22rem] overflow-hidden rounded-[2rem] border border-border bg-card shadow-xl shadow-primary/10 lg:ml-auto lg:mr-0">
        <img
          src="/ai-voice-workflow-hero.png"
          alt="Enterprise workflow for AI calls and WhatsApp messages, with structured screening checks and recruiter evidence review"
          className="block aspect-square w-full object-cover"
        />
        <figcaption className="absolute inset-x-3 bottom-3 rounded-2xl border border-white/80 bg-background/95 p-3 shadow-lg shadow-foreground/10 backdrop-blur-sm sm:inset-x-5 sm:bottom-5 sm:p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Screening workflow</p>
          <ol className="mt-3 grid grid-cols-3 divide-x divide-border">
            <li className="min-w-0 px-2 first:pl-0" aria-label="AI calls and WhatsApp messages">
              <span className="flex h-8 items-center gap-1" aria-hidden="true">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary ring-2 ring-background">
                  <PhoneCall className="h-3.5 w-3.5" />
                </span>
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#25D366]/15 text-[#128C4A] ring-2 ring-background">
                  <FaWhatsapp className="h-3.5 w-3.5" />
                </span>
              </span>
              <span className="mt-1.5 block whitespace-nowrap text-[11px] font-semibold leading-snug text-foreground sm:text-xs">Call &amp; chat</span>
            </li>
            <li className="min-w-0 px-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <ClipboardCheck className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
              <span className="mt-1.5 block text-[11px] font-semibold leading-snug text-foreground sm:text-xs">Role questions</span>
            </li>
            <li className="min-w-0 px-2 pr-0">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <UserCheck className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
              <span className="mt-1.5 block text-[11px] font-semibold leading-snug text-foreground sm:text-xs">Recruiter review</span>
            </li>
          </ol>
        </figcaption>
      </figure>
    </aside>
  );
}

function ConversationFlow() {
  return (
    <Section id="candidate-conversation" muted compact>
      <SectionIntro
        fullWidth
        eyebrow="Candidate conversation"
        title="A structured conversation, with the recruiter still in control."
        copy="Voice screening can run an HR or technical conversation. WhatsApp supports opt-in screening Q&A and a recruiter-accessible conversation inbox."
      />
      <IndustryWorkflow steps={CONVERSATION_FLOW} plainDetail />
    </Section>
  );
}

function VerifiedChecks() {
  return (
    <Section compact>
      <SectionIntro
        fullWidth
        eyebrow="Verified information"
        title="Check the hiring details that the current voice flow supports."
        copy="Only the available call and WhatsApp screening signals are shown here. The recruiter can review the underlying conversation and evaluation."
      />
      <VerifiedInformationPanel />
    </Section>
  );
}

function VerifiedInformationPanel() {
  return (
    <div className="mt-8 overflow-hidden rounded-2xl border border-primary/20 bg-[hsl(158_45%_98%)] shadow-sm">
      <div className="grid sm:grid-cols-2">
      {VERIFIED_GROUPS.map((group, groupIndex) => (
        <section
          key={group.label}
          aria-labelledby={`verified-group-${groupIndex}`}
          className={`px-5 py-5 sm:px-6 sm:py-6 ${groupIndex > 0 ? 'border-t border-primary/15 sm:border-l sm:border-t-0' : ''}`}
        >
          <h3 id={`verified-group-${groupIndex}`} className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">
            {group.label}
          </h3>
          <ul className="mt-4 divide-y divide-primary/15">
            {group.items.map((item) => (
              <li key={item.title} className="relative py-4 pl-5">
                <span className="absolute left-0 top-[1.05rem] h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                <p className="text-base font-semibold leading-snug text-foreground">{item.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.detail}</p>
              </li>
            ))}
          </ul>
        </section>
      ))}
      </div>
    </div>
  );
}

function Outcomes() {
  return (
    <Section muted compact>
      <SectionIntro
        fullWidth
        eyebrow="Screening outcomes"
        title="Every conversation leaves a useful next step."
        copy="Automation captures the outcome. The hiring team remains responsible for pipeline and hiring decisions."
      />
      <ProblemGrid items={OUTCOMES} highlighted />
    </Section>
  );
}

